import { Fragment } from 'react';
import { Skeleton } from '../Skeleton';
import { ProgressPie } from '../ProgressPie';
import type { MenuItem } from '../Menu';
import { AnchorCellContext } from '../table/anchorCell';
import { ListSurfaceContext } from '../table/listSurface';
import { RowActionMenu } from '../table/RowActionMenu';
import { LatestUpdateStamp } from '../table/LatestUpdateCell';
import { RowQuickActions } from '../table/RowQuickActions';
import type { QuickActionStyle } from '../table/rowQuickActionWidth';
import type { OtherInfoData } from '../table/OtherInfoModal';
import { formatDateTime, toIso } from '../../lib/formatDate';
import type { ListAction, ColumnDef } from './types';
import { Checkbox } from '../inputs/Checkbox';

/** Hard cap on the sub-information line. A fourth value stops being a summary. */
const MAX_SUMMARY_VALUES = 3;

/**
 * The **list view** — reference §3, the second of the two shapes `<IndexPage>` offers.
 *
 * A list row is a summary, not a table row with the grid taken away. It carries exactly two
 * stacks a side:
 *
 * ```
 *  ☐  1  ⬤  Title                                                       active   ⋮
 *           Created by Someone · 08 Sep 2026, 17:33 · value · value
 * ```
 *
 * **Left is the record**: an avatar spanning both lines where the anchor supplies one, then
 * the title (the anchor column, and the same click target as in the table) over one line
 * that opens with who made it and when, followed by the one to three most important values.
 * **Right is its state**: the status badge alone.
 *
 * `provenanceLead={false}` drops the "Created by …" opening, leaving the summary values on
 * their own — for a list whose own columns already carry the actor and the instant.
 *
 * Reference §3 requires the list and table views to surface the same *fields*, and they do —
 * the list reads the same `columns` and the same `otherInfo`. What it does not do is render
 * all of them: fourteen labelled micro-columns squeezed into a row is a worse table, not a
 * list.
 *
 * **Multi-select is the same control on both views.** `selectable` puts the checkbox in the
 * leading gutter, ahead of the row number, exactly where the table has it, and both read the
 * one `selectedKeys` the shell owns — so a selection survives a view switch.
 */
export function IndexListView<T>({
  module,
  columns,
  rows,
  rowKey,
  firstRow,
  loading,
  otherInfo,
  listAction = 'created',
  provenanceLead = true,
  latestUpdate = true,
  rowActions,
  rowQuickActions,
  rowQuickActionStyle = 'label',
  rowProgress,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  onRowOpen,
  selectable = false,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  rowTone,
  hideStatus = false,
}: {
  module: string;
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  firstRow: number;
  loading: boolean;
  otherInfo: (row: T) => OtherInfoData | null;
  listAction?: ListAction;
  /**
   * Whether the summary line opens with who made the record and when.
   *
   * Off for a list whose own columns already ARE the creation metadata — the audit trail,
   * whose anchor is the actioner and whose second column is the instant. `<IndexPage>` wires
   * it from the same `auditColumns` flag that drops the table's Created / Created by pair,
   * so the two views cannot disagree about whether provenance is already on screen.
   */
  provenanceLead?: boolean;
  /** False drops the Latest update stamp — see `IndexPageProps.latestUpdate`. */
  latestUpdate?: boolean;
  rowActions?: (row: T) => MenuItem[];
  /**
   * A row's most-used actions, as small labelled buttons before the kebab — see `<RowQuickActions>`.
   *
   * Entries here are PROMOTED OUT of `rowActions`, never duplicated into it: one action in two
   * places on one row is what this product refuses everywhere else.
   */
  rowQuickActions?: (row: T) => MenuItem[];
  /** `icon` is the Repository exception — see `IndexPageProps.rowQuickActionStyle`. */
  rowQuickActionStyle?: QuickActionStyle;
  /**
   * How far through fulfilment the row is, drawn as a pie before the kebab — the FULFILMENT
   * list format. See `IndexPageProps.rowProgress`, which is where the rule lives.
   *
   * A row returning null draws nothing at all, so a register carries the pie on the records
   * being worked off and leaves the drafts beside them alone.
   */
  rowProgress?: (row: T) => { value: number; total: number; label: string } | null;
  onEdit?: (row: T) => void;
  editPermission?: string;
  onDelete?: (row: T) => void;
  deletePermission?: string;
  deleteLabel?: (row: T) => string;
  deleteDisabledReason?: (row: T) => string | null;
  onRowOpen?: (row: T) => void;
  /** Same prop, same meaning, same gutter position as `<IndexTable>`. */
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleRow?: (key: string) => void;
  onToggleAll?: () => void;
  /** The table's data-state tint, on the list row too — one prop, both views. */
  rowTone?: (row: T) => 'red' | null;
  /**
   * Drop the status badge, because every row on screen carries the same one.
   *
   * LIST ONLY — the table keeps its column. A grid is scanned DOWN, where a uniform column
   * costs a header and nothing else; a list row is read ACROSS, and a pill repeating a filter
   * the reader just set takes the row's scarcest space.
   *
   * The shell decides, in `IndexPage` — see `listStatusIsUniform`, which is the rule.
   */
  hideStatus?: boolean;
}) {
  if (loading) {
    return (
      <ul className="divide-y divide-slate-200">
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className="px-3 py-3.5">
            <Skeleton className="h-4 w-1/3" />
          </li>
        ))}
      </ul>
    );
  }

  // The title is the column that asks to be the anchor, else the first — as every list had before the flag.
  const anchor = columns.find((column) => column.anchor) ?? columns[0];
  const rest = columns.filter((column) => column !== anchor);

  /*
    `hideStatus` drops the BADGE only. The column still counts as the status column for the
    summary line below, which excludes it on `column.status` rather than on this variable —
    suppressing a pill must not push its value into the sub-information line, where it would
    arrive as a bare `· active` with no label at all.
  */
  const status = hideStatus ? undefined : rest.find((column) => column.status);

  /*
    THE RECORD'S OWN LIFECYCLE, BESIDE THE BADGE — product call, 29 Sep 2026: *if a transaction
    has an approval process, display also the approval status, beside the "active" status
    card/table view*, and then *this should be in the card list to.*

    It is drawn here rather than left to the summary line, and that is the whole reason the flag
    exists: the summary concatenates its values with NO LABELS, so a lifecycle arriving there
    reads as a bare `· For approval` that a reader cannot interpret — the same objection that
    keeps a COUNT out of that line. Beside the badge it sits where the reader already looks for
    what a record IS.

    LEADING the badge, so `active` stays pinned at the row's trailing edge where it has always
    been, and the pair reads lifecycle-then-row-state — the order the TABLE puts them in.
  */
  const stage = rest.find((column) => column.stage);

  /*
    Which values make the sub-information line.

    A page can nominate them with `summary: true`. Without that the first few columns after
    the title are used, which is a real default rather than a placeholder: column order is
    already an importance ordering, so the columns a page put first are the ones it thinks
    matter. Excluded either way are status and the shell's own Created / Created by — all
    three live in the right-hand stack.
  */
  const nominated = rest.filter((column) => column.summary);

  const summary = (
    nominated.length > 0
      ? nominated
      : rest.filter(
          (column) =>
            /*
              The shell's audit pair is excluded by KEY. `updatedAt` joined the list on
              21 Oct 2026, when it became a STACKED column — a two-line cell dropped into this
              one-line truncated flow would have rendered its actor mid-sentence. `createdByName`
              is kept in the list though its column is gone, so a panel still declaring one of
              its own is excluded too.
            */
            !column.status &&
            column.key !== 'createdAt' &&
            column.key !== 'createdByName' &&
            column.key !== 'updatedAt',
        )
  ).slice(0, MAX_SUMMARY_VALUES);

  const selected = selectedKeys ?? new Set<string>();
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const someSelected = rows.some((r) => selected.has(rowKey(r))) && !allSelected;

  return (
    <ListSurfaceContext.Provider value={true}>
      {/*
        Select-all. The table carries it in its header row and a list has no header, so it
        gets one strip of its own — tinted like the table's `<thead>` so the two views read
        as the same control in the same place. Without it, switching to the list would
        silently drop the ability to select a page at a time.

        The count is deliberately not repeated here: `<IndexSelectionBar>` already states it,
        and printing it twice is the same value in two slots.

        The LABEL appears on hover and on focus; the box is always drawn. A select-all sits
        at the top-left of a list of rows, which is where every reader already looks for one,
        so the words are a reminder rather than the affordance — and standing permanently
        above every list they are one more piece of chrome competing with the records.
      */}
      {selectable ? (
        <div className="group flex items-center gap-4 border-b border-slate-200 bg-slate-100 px-3 py-2">
          <Checkbox
            id={`${module}-select-all`}
            name={`${module}-select-all`}
            checked={allSelected}
            indeterminate={someSelected}
            onChange={onToggleAll}
            aria-label="Select all rows on this page"
          />
          {/*
            `opacity-0`, never `hidden`: the strip keeps its height either way, so the rows
            below do not jump as the pointer crosses it.

            Revealed on FOCUS as well as hover — a keyboard reader tabs to the box and never
            generates a hover, and a label they can never see is the failure the tree's lane
            actions already document.

            `aria-hidden` because the checkbox beside it already carries this exact sentence
            as its `aria-label`. Left readable it would be announced twice, which is the
            same-value-in-two-slots rule one layer down.
          */}
          <span
            aria-hidden="true"
            className="text-[9px] font-normal uppercase tracking-wider text-gray-600 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
          >
            Select all on this page
          </span>
        </div>
      ) : null}

      {/* One continuous box: rows are divided, not spaced. The panel frame around this
          list is the only edge, so a row draws no border, no radius and no shadow. */}
      <ul className="divide-y divide-slate-200">
        {rows.map((row, index) => {
          const key = rowKey(row);
          const audit = otherInfo(row);

          const actor = listAction === 'updated' ? audit?.updatedByName : audit?.createdByName;
          const at = listAction === 'updated' ? audit?.updatedAt : audit?.createdAt;

          /*
            A row with NO provenance drops the lead, whatever the flag says. `auditColumns`
            and a null `otherInfo` travel together by convention; deriving it here as well is
            what stops a page that sets one and forgets the other printing "Created by —".
          */
          const lead = provenanceLead && audit !== null;
          const isSelected = selected.has(key);

          return (
            <li
              key={key}
              /* Hover tints the row, exactly as a table row does — the two views are the
               same list in different clothes, so they answer the pointer the same way.
               A selected row carries the same wash the table gives it. */
              className={`flex items-center gap-4 px-3 py-3 transition ${
                isSelected
                  ? 'bg-blue-50/40 hover:bg-slate-50'
                  : rowTone?.(row) === 'red'
                    ? 'bg-red-50 hover:bg-red-100'
                    : 'hover:bg-slate-50'
              }`}
            >
              {/* Leading gutter, ahead of the row number — the table's order exactly. */}
              {selectable ? (
                <Checkbox
                  id={`${module}-select-${key}`}
                  name={`${module}-select-${key}`}
                  checked={isSelected}
                  onChange={() => onToggleRow?.(key)}
                  aria-label="Select row"
                />
              ) : null}

              <span className="w-8 shrink-0 font-mono tabular-nums text-xs text-slate-400">
                {firstRow + index}
              </span>

              {/*
              Left: the record. The avatar is hoisted out of the anchor and stands to the
              left of BOTH lines — a list row is two lines tall, so an avatar pinned to the title
              line alone leaves the summary hanging under it with nothing to align to.
              `<AvatarCell>` drops its own copy inside this surface.
            */}
              {anchor.avatar ? <span className="shrink-0">{anchor.avatar(row)}</span> : null}

              <div className="min-w-0 flex-1">
                <div className="min-w-0">
                  {/*
                    THE TONE FOLLOWS THE AFFORDANCE, so the provider is `!!onRowOpen` rather
                    than `true`. `<DataTable>` already gates its own on the same thing and its
                    comment says why — "one provider, both surfaces" — and this half did not,
                    so a surface with no anchor rendered its title in the anchor blue, at
                    `font-medium`, opening nothing. That is the dead affordance the hide rule
                    forbids, and it was unreachable only because every caller happened to
                    supply `rowPreview` or `onRowOpen`.
                  */}
                  <AnchorCellContext.Provider value={Boolean(onRowOpen)}>
                    {onRowOpen ? (
                      <button
                        type="button"
                        id={`${module}-list-open-${key}`}
                        onClick={() => onRowOpen(row)}
                        className="group/anchor block w-full min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                      >
                        {anchor.render(row, firstRow + index)}
                      </button>
                    ) : (
                      anchor.render(row, firstRow + index)
                    )}
                  </AnchorCellContext.Provider>
                </div>

                {/*
                Under the title: who made the record and when, FIRST, then the summary
                values. Provenance leads the line because on a list row the question is "what is
                this and where did it come from" — and it reads under the title it belongs
                to rather than across the row from it.

                List view only. The table keeps its own Created / Created by columns.
              */}
                {/*
                Capped at 65% of the row. Left uncapped, a long value pushes the status and
                the update stamp off the right edge — the two things read down a column of
                rows.

                ONE line, ONE ellipsis. This is a single inline flow with `truncate` on the
                container, not a flex row of individually-truncating parts: that produced an
                ellipsis mid-line with more content rendered after it, which reads as a bug.
                The `[&_*]` rules force nested cells inline and onto this line's own size and
                colour — `<TextCell>` renders a `block` span at its own tone, which would
                otherwise break the flow onto its own row and arrive a shade darker.
              */}
                {/* Nothing to say under the title: no line, so no empty line of height. */}
                {lead || summary.length > 0 ? (
                  <p
                    id={`${module}-list-summary-${key}`}
                    className="mt-0.5 max-w-[65%] truncate text-xs text-slate-400 [&_*]:!inline [&_*]:!text-xs [&_*]:!text-slate-400"
                  >
                    {lead ? (
                      <>
                        {listAction === 'updated' ? 'Updated by' : 'Created by'} {actor ?? '—'}
                        {at ? (
                          <>
                            <span aria-hidden="true" className="px-1">
                              ·
                            </span>
                            {/* Dates stay monospaced even inline — reference §4. */}
                            <time dateTime={toIso(at)} className="font-mono tabular-nums">
                              {formatDateTime(at)}
                            </time>
                          </>
                        ) : null}
                      </>
                    ) : null}

                    {summary.map((column, position) => (
                      <Fragment key={column.key}>
                        {/* A separator only where something precedes it, or the line opens
                          with a stray middot once the provenance lead is off. */}
                        {lead || position > 0 ? (
                          <span aria-hidden="true" className="px-1">
                            ·
                          </span>
                        ) : null}
                        {column.render(row, firstRow + index)}
                      </Fragment>
                    ))}
                  </p>
                ) : null}
              </div>

              {/*
              Right: the record's state, over when it last changed. Creation provenance
              moved under the title; what stays here is the pair read down a column of
              rows — what this record is now, and how recently that changed.
            */}
              <div className="hidden shrink-0 text-right sm:block">
                {stage || status ? (
                  <div className="flex items-center justify-end gap-2">
                    {stage ? (
                      /*
                        RENDERED AS THE COLUMN GIVES IT, with no wrapper of its own — product
                        call, 01 Oct 2026: *card list approval status should also be a badge.*

                        It used to be wrapped in `text-xs text-slate-500`, which was the whole
                        of what made the lifecycle plain text here. The badge is the COLUMN's
                        now, so the wrapper had to go: a `<StatusText>` inside a muted 13px
                        span would inherit a size and a tone fighting the pill's own 11px mono.

                        That reverses *"PLAIN TEXT, never a second pill"*, which argued that
                        two badges side by side make a reader read both to learn one thing.
                        See CLAUDE.md, where the reversal and its cost are recorded.
                      */
                      stage.render(row, firstRow + index)
                    ) : null}
                    {status ? status.render(row, firstRow + index) : null}
                  </div>
                ) : null}

                {/*
                Nothing at all on a record never touched since creation — not a line of text
                saying so, and not an empty <p> either, which would hold a line of height the
                badge above then floats over.

                The absence is the statement, the same way the table's Latest update column is
                simply blank. Spelling it out put a sentence on every row of a freshly seeded
                list, competing with the status badge for a fact nobody asked about.

                CLICKABLE under `audit-logs.read`, opening the audit entry behind the stamp —
                the same component the table column draws, because the two views reading one
                `columns` and then differing on what a stamp DOES is the defect that sharing
                exists to prevent.
              */}
                {latestUpdate ? <LatestUpdateStamp entityId={audit?.auditEntityId ?? key}updatedAt={audit?.updatedAt ?? null} /> : null}
              </div>

              {/*
                THE FULFILMENT FORMAT — a wedge over its own percentage, between the record's
                state and the controls at the trailing edge.

                It sits on the FACT side of that boundary deliberately. A pie wedged between
                the quick actions and the kebab would be a third thing in a run of controls and
                would read as one; here it closes the run of facts the state badges begin, which
                is what it is — how far through this record is.

                THE SLOT IS RESERVED on every row of a register that declares the callback, and
                a row that cannot answer leaves it EMPTY rather than closing it up. It shipped
                the other way for an hour and the cost was immediate on screen: a ring takes
                ~44px, so the status badge and the update stamp — the two things a list is read
                DOWN a column for — stepped left on the rows carrying one and back out on the
                drafts between them. That is the row-height rule read along the other axis; a
                ragged column costs the eye the vertical line it scans just as a ragged row
                costs it the horizontal one. Measured after: the badge's right edge is identical
                on every row of the register.

                `w-11` is 44px against the `sm` ring's 40px, which leaves it two pixels either
                side. **The width is the RING's, not the figure's** — that is what changed when
                the figure moved inside the hole on 01 Oct 2026. It used to be `w-9`, sized to a
                `100%` caption that was wider than the disc above it and half the width of a `0%`
                one; a ring is a square of one size whatever it reads, so the slot now follows a
                constant rather than defending against a variable.

                The LIST owns the box and the component owns the ring.
              */}
              {rowProgress ? (() => {
                const progress = rowProgress(row);

                return (
                  <div className="hidden w-11 shrink-0 justify-center sm:flex">
                    {progress ? (
                      <ProgressPie
                        value={progress.value}
                        total={progress.total}
                        label={progress.label}
                        format="percent"
                        /* Sized to the row's own content block, so a row carrying one is exactly
                           as tall as the rows between them — see the prop. */
                        size="sm"
                      />
                    ) : null}
                  </div>
                );
              })() : null}

              {/*
                Beside the kebab, at the row's trailing edge — the same place and the same order
                the table puts them, so a reader switching view finds one arrangement.
              */}
              {rowQuickActions ? (
                <RowQuickActions
                  id={`${module}-list-quick-${key}`}
                  items={rowQuickActions(row)}
                  style={rowQuickActionStyle}
                />
              ) : null}

              <RowActionMenu
                id={`${module}-list-actions-${key}`}
                otherInfo={audit}
                extraItems={rowActions?.(row)}
                onEdit={onEdit ? () => onEdit(row) : undefined}
                editPermission={editPermission}
                onDelete={onDelete ? () => onDelete(row) : undefined}
                deletePermission={deletePermission}
                deleteLabel={deleteLabel?.(row)}
                deleteDisabledReason={deleteDisabledReason?.(row) ?? null}
              />
            </li>
          );
        })}
      </ul>
    </ListSurfaceContext.Provider>
  );
}
